import React from 'react';
import { Box, Typography, Button, Container, Grid, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import rackingHeroBg from '../images/Racking.jpg';

const F1RackingHero = () => {
  const f1StreakVariants = {
    initial: { offsetDistance: '0%', opacity: 0 },
    animate: {
      offsetDistance: '100%',
      opacity: [0, 1, 1, 0],
      transition: { duration: 2.5, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1 },
    },
  };

  return (
    <Box
      sx={{
        minHeight: { xs: '100dvh', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        pt: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${rackingHeroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: { xs: 'center', md: 'center right' },
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          background: {
            xs:
              'linear-gradient(180deg, rgba(12,12,14,0.88) 0%, rgba(12,12,14,0.72) 45%, rgba(12,12,14,0.55) 100%)',
            md:
              'linear-gradient(105deg, rgba(12,12,14,0.92) 0%, rgba(12,12,14,0.78) 38%, rgba(12,12,14,0.45) 58%, rgba(12,12,14,0.22) 100%)',
          },
        }}
      />
      <svg width="100%" height="100%" style={{ position: 'absolute', zIndex: 2, pointerEvents: 'none', opacity: 0.35 }}>
        <path id="f1Path" d="M -100,500 Q 400,400 1200,-100" fill="transparent" />
      </svg>

      <Box
        component={motion.div}
        variants={f1StreakVariants}
        initial="initial"
        animate="animate"
        sx={{
          position: 'absolute',
          width: 120,
          height: 12,
          bgcolor: '#FF6B00',
          borderRadius: '20px 0 0 20px',
          boxShadow: '-20px 0px 40px #FF6B00',
          zIndex: 3,
          offsetPath: "path('M -100,500 Q 400,400 1500,100')",
        }}
      />

      <Box
        component={motion.div}
        animate={{
          rotateX: [0, 360],
          rotateY: [0, 180],
          y: [0, -20, 0],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        sx={{
          position: 'absolute',
          top: '10%',
          right: '20%',
          width: 60,
          height: 60,
          border: '4px solid rgba(255,255,255,0.35)',
          bgcolor: 'rgba(255, 107, 0, 0.15)',
          perspective: 1000,
          transformStyle: 'preserve-3d',
          boxShadow: 'inset 0 0 15px rgba(255,107,0,0.45), 10px 10px 24px rgba(0,0,0,0.35)',
          zIndex: 2,
          display: { xs: 'none', sm: 'block' },
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          zIndex: 10,
          position: 'relative',
          pt: { xs: 'calc(90px + 1.5rem)', md: 'calc(92px + 2rem)' },
          pb: { xs: 6, md: 8 },
        }}
      >
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7} lg={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  color: '#ffffff',
                  mb: 2,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3.25rem' },
                  lineHeight: 1.08,
                  textShadow: '0 2px 24px rgba(0,0,0,0.35)',
                }}
              >
                Custom racking that unlocks more space in your warehouse.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  mb: 4,
                  maxWidth: '480px',
                  borderLeft: '4px solid #FF6B00',
                  pl: 2,
                  textShadow: '0 1px 8px rgba(0,0,0,0.35)',
                }}
              >
                Engineered steel profiles, load-aware layouts, and storage you can trust — built on roll
                forming and section development expertise.
              </Typography>

              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Button
                  component={RouterLink}
                  to={{ pathname: '/', hash: '#requirement-form' }}
                  variant="contained"
                  sx={{
                    bgcolor: '#FF6B00',
                    color: '#fff',
                    borderRadius: 0,
                    px: 4,
                    py: 1.5,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
                    '&:hover': { bgcolor: '#e65f00', color: '#fff' },
                  }}
                >
                  Start a project
                </Button>
                <Button
                  component={RouterLink}
                  to="/products"
                  variant="outlined"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.85)',
                    color: '#fff',
                    borderRadius: 0,
                    px: 4,
                    py: 1.5,
                    bgcolor: 'rgba(0,0,0,0.15)',
                    '&:hover': {
                      borderColor: '#FF6B00',
                      color: '#FF6B00',
                      bgcolor: 'rgba(0,0,0,0.25)',
                    },
                  }}
                >
                  View products
                </Button>
              </Stack>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default F1RackingHero;
